export type StoreDeliveryServiceType =
  | "PRIORITY"
  | "STANDARD"
  | "RUSH"
  | "ECONOMY";

export type StoreDeliveryServiceStatus = "draft" | "active" | "inactive";

export interface StoreDefaultService {
  settingsType: string;
  name: string;
  description: string;
  cutoffTime: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  globallyActive: boolean;
  enabledForStore: boolean;
}

export interface StorePricingCatalog {
  defaultServices: StoreDefaultService[];
  storeServices: StoreDeliveryService[];
  hasActiveStoreServices: boolean;
}

export interface StoreServicePricing {
  currency: string;
  hst: number;
  flatPricingEnabled: boolean;
  flatPrice: number;
  includedDistanceKm: number;
  additionalKmPrice: number;
  vehicleChargesEnabled: boolean;
  vehicleSurcharges: {
    car: number;
    suv: number;
    minivan: number;
  };
  weightChargesEnabled: boolean;
  freeWeightUpToLb: number;
  additionalPricePerLb: number;
  taxEnabled: boolean;
}

export interface StoreServiceTerritoryArea {
  areaKey: string;
  label: string;
  fsaCodes: string[];
  surchargeCad: number;
}

export interface StoreDeliveryService {
  _id?: string;
  id?: string;
  zuluStoreId?: string;
  name: string;
  description: string;
  serviceType: StoreDeliveryServiceType;
  status: StoreDeliveryServiceStatus;
  cutoffTime: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  pricing: StoreServicePricing;
  territoryName: string;
  territoryAreas: StoreServiceTerritoryArea[];
  masterServiceCode?: string | null;
  activatedAt?: string | null;
}

export interface StoreDeliveryServiceList {
  serviceList: StoreDeliveryService[];
  count: number;
}

export interface MasterServiceTemplate {
  code: string;
  name: string;
  description: string;
  serviceType: StoreDeliveryServiceType;
  cutoffTime: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
}

export const STORE_SERVICE_TEMPLATES: MasterServiceTemplate[] = [
  {
    code: "same_day",
    name: "Same Day Delivery",
    description: "Order before cutoff to receive delivery the same day.",
    serviceType: "PRIORITY",
    cutoffTime: "13:00",
    deliveryWindowStart: "13:00",
    deliveryWindowEnd: "21:00",
  },
  {
    code: "next_day",
    name: "Next Day Delivery",
    description: "Reliable delivery by the end of the next business day.",
    serviceType: "STANDARD",
    cutoffTime: "13:00",
    deliveryWindowStart: "09:00",
    deliveryWindowEnd: "21:00",
  },
  {
    code: "rush",
    name: "Rush Delivery",
    description: "Priority delivery for time-sensitive orders.",
    serviceType: "RUSH",
    cutoffTime: "15:00",
    deliveryWindowStart: "15:00",
    deliveryWindowEnd: "19:00",
  },
  {
    code: "economy",
    name: "Economy Delivery",
    description: "Cost-effective delivery for flexible timelines.",
    serviceType: "ECONOMY",
    cutoffTime: "11:00",
    deliveryWindowStart: "09:00",
    deliveryWindowEnd: "21:00",
  },
];

export const emptyStoreDeliveryService = (): StoreDeliveryService => ({
  name: "",
  description: "",
  serviceType: "PRIORITY",
  status: "draft",
  cutoffTime: "13:00",
  deliveryWindowStart: "13:00",
  deliveryWindowEnd: "21:00",
  pricing: {
    currency: "USD",
    hst: 13,
    flatPricingEnabled: true,
    flatPrice: 25,
    includedDistanceKm: 50,
    additionalKmPrice: 1.2,
    vehicleChargesEnabled: true,
    vehicleSurcharges: { car: 0, suv: 10, minivan: 20 },
    weightChargesEnabled: true,
    freeWeightUpToLb: 50,
    additionalPricePerLb: 0.2,
    taxEnabled: true,
  },
  territoryName: "",
  territoryAreas: [],
});

